import { BehaviorSubject } from 'rxjs';
import { mapQuery } from './queryMappers';
import { STORE_EVENT_BUS } from './storeEvents';
import { setInternalStoreFlag, setStateMutationName } from './storeMetadata';
import { DEFAULT_COMPARATOR, isReadonlyArray } from './utils';
let STORE_SEQ_NUMBER = 0;
export function declareStateUpdates(stateExample, updates) {
    if (updates) {
        return updates;
    }
    return (updates) => updates;
}
/**
 * Returns a mutation which applies all provided mutations for a state.
 *
 * You can use this helper to apply multiple changes at the same time.
 */
export function pipeStateMutations(mutations) {
    return (state) => mutations.reduce((nextState, mutation) => mutation(nextState), state);
}
/**
 * Creates the state store.
 *
 * @param initialState Initial state
 * @param options Parameters for the store
 */
export function createStore(initialState, options) {
    var _a;
    const stateComparator = (_a = options === null || options === void 0 ? void 0 : options.comparator) !== null && _a !== void 0 ? _a : DEFAULT_COMPARATOR;
    const store$ = new BehaviorSubject(initialState);
    const state$ = store$.asObservable();
    let isUpdating = false;
    let pendingMutations;
    const store = {
        id: ++STORE_SEQ_NUMBER,
        name: options === null || options === void 0 ? void 0 : options.name,
        value$: state$,
        get() {
            return store$.value;
        },
        select(selector, options) {
            return this.query(selector, options).value$;
        },
        query(selector, options) {
            return mapQuery(this, selector, options);
        },
        asQuery: () => store,
        set(nextState) {
            apply([() => nextState]);
        },
        update,
        destroy() {
            var _a;
            store$.complete();
            STORE_EVENT_BUS.next({ type: 'destroyed', store });
            (_a = options === null || options === void 0 ? void 0 : options.onDestroy) === null || _a === void 0 ? void 0 : _a.call(options);
        },
    };
    function update(mutation) {
        if (isReadonlyArray(mutation)) {
            apply(mutation);
        }
        else {
            apply([mutation]);
        }
    }
    function apply(mutations) {
        if (isUpdating) {
            pendingMutations = (pendingMutations !== null && pendingMutations !== void 0 ? pendingMutations : []).concat(mutations);
            return;
        }
        const prevState = store$.value;
        let nextState = prevState;
        for (let i = 0; i < mutations.length; i++) {
            const mutation = mutations[i];
            if (mutation) {
                const stateBeforeMutation = nextState;
                nextState = mutation(nextState);
                STORE_EVENT_BUS.next({
                    type: 'mutation',
                    store,
                    mutation,
                    nextState,
                    prevState: stateBeforeMutation,
                });
            }
        }
        if (stateComparator(prevState, nextState)) {
            return;
        }
        isUpdating = true;
        store$.next(nextState);
        STORE_EVENT_BUS.next({
            type: 'updated',
            store,
            nextState,
            prevState,
        });
        isUpdating = false;
        if (pendingMutations === null || pendingMutations === void 0 ? void 0 : pendingMutations.length) {
            const mutationsToApply = pendingMutations;
            pendingMutations = [];
            apply(mutationsToApply);
        }
    }
    if (options === null || options === void 0 ? void 0 : options.internal) {
        setInternalStoreFlag(store);
    }
    STORE_EVENT_BUS.next({ type: 'created', store });
    return store;
}
/** Creates StateUpdates for updating the store by provided state mutations */
export function createStoreUpdates(storeUpdate, stateUpdates) {
    const updates = {};
    Object.entries(stateUpdates).forEach(([key, mutationFactory]) => {
        updates[key] = (...args) => {
            const mutation = mutationFactory(...args);
            setStateMutationName(mutation, key);
            storeUpdate(mutation);
        };
    });
    return updates;
}
/** Creates a proxy for the store with "updates" to change a state by provided mutations */
export function withStoreUpdates(store, updates) {
    const storeUpdates = createStoreUpdates(store.update, updates);
    return { ...store, updates: storeUpdates };
}
//# sourceMappingURL=store.js.map